import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import MigrationCrunchFooter from '../components/MigrationCrunchFooter';
import {
  Sparkle,
  CheckCircle,
  X,
  WarningCircle,
  ArrowRight,
  Brain,
  CurrencyDollar,
  Lightning,
  Lock,
  Database,
  Scales,
} from '@phosphor-icons/react';
import { COMPETITORS as VS_COMPETITORS } from '../data/competitors';
import PrintableComparison from '../components/PrintableComparison';

/**
 * /migrate/compare — public comparison landing page.
 *
 * SEO target queries:
 *   "best crm migration tool 2026"
 *   "migratemycrm vs trujay"
 *   "import2 vs migrationcrunch"
 *   "crm migration software comparison"
 *
 * Positioning: MigrationCrunch is the ONLY tool that solves the
 * "unstructured-data-to-structured-fields" problem at a self-service price.
 * Every claim on this page must be defensible — either a verifiable fact
 * (public pricing) or framed as an architectural/approach difference.
 */

/**
 * Per-tier breakdown — every MigrationCrunch tier alongside the nearest
 * competitor equivalent matched by data volume. This is the data behind
 * the "Each tier, each competitor, side by side" table that lives between
 * the qualitative competitor table and the "Four things only we do" section.
 *
 * Source for competitor prices: see /app/frontend/src/data/competitors.js
 * (kept in one place so future pricing updates only touch that file).
 */
const TIER_BREAKDOWN = [
  {
    ours: { name: 'Free Preview', price: '$0', range: '≤ 50 contacts + notes' },
    migratemycrm: { name: '— (trial only)', price: 'n/a' },
    import2: { name: '— (paid only)', price: 'from ~$99' },
    trujay: { name: 'Sample demo', price: 'Free demo' },
    slalom: { name: '— (below floor)', price: 'n/a' },
  },
  {
    ours: { name: 'Contacts Only', price: '$299', range: '≤ 12,500 contacts (no notes)' },
    migratemycrm: { name: 'Starter', price: '$349 (~800-1,000 contacts once history counted)' },
    import2: { name: 'Standard base', price: '$499 (~2,500-3,500 contacts once history counted)' },
    trujay: { name: '— (below floor)', price: 'n/a' },
    slalom: { name: '— (below floor)', price: 'n/a' },
  },
  {
    ours: { name: 'Starter Pro', price: '$599', range: '≤ 12,500 contacts' },
    migratemycrm: { name: 'Plus', price: '~$299 – $999' },
    import2: { name: 'Standard', price: '$99 – $499 (≤ ~2.5k)' },
    trujay: { name: 'Basic project', price: '~$1,500 – $3,000' },
    slalom: { name: '— (below floor)', price: 'n/a' },
  },
  {
    ours: { name: 'Growth AI', price: '$1,799', range: '12,501 – 100,000 contacts' },
    migratemycrm: { name: 'Pro / Custom', price: '~$1,000 – $2,000' },
    import2: { name: 'Custom', price: 'Custom quote' },
    trujay: { name: 'Mid-tier project', price: '~$3,500 – $5,000' },
    slalom: { name: '— (below floor)', price: 'n/a' },
  },
  {
    ours: {
      name: 'Enterprise AI',
      price: '$4,499',
      range: '100,001 – 500,000 contacts',
    },
    migratemycrm: { name: 'Premium / Custom Service', price: '~$2,199+' },
    import2: { name: 'Custom', price: 'Custom quote' },
    trujay: { name: 'Premium project', price: '~$5,000 – $9,000' },
    slalom: { name: 'Smallest engagement', price: '~$25,000 – $50,000' },
  },
  {
    ours: { name: 'Unlimited AI', price: '$7,999', range: '500,000+ contacts' },
    migratemycrm: { name: 'Custom Service', price: 'Custom quote' },
    import2: { name: '— (out of scope)', price: 'Custom' },
    trujay: { name: 'Custom enterprise', price: 'Custom quote' },
    slalom: { name: 'Standard enterprise project', price: '$75k – $150k+' },
  },
];

const COMPETITORS = [
  {
    id: 'migrationcrunch',
    name: 'MigrationCrunch',
    isUs: true,
    focus: 'AI-powered, recovers data from notes',
    pricing: 'Flat $0 – $7,999 (one-time)',
    notesHandling: 'AI recovers entities from text into structured fields',
    selfService: true,
    handlesUnstructured: true,
    timeToMigrate: 'Minutes',
    bestFor: 'Anyone who has 20 years of typed notes they refuse to lose',
  },  {
    id: 'migratemycrm',
    name: 'MigrateMyCRM (SyncMatters)',
    focus: 'Self-service auto-mapping for 25+ CRMs',
    pricing: '$349 – $2,999 per run',
    notesHandling: 'Dumps note text into a "Description" blob',
    selfService: true,
    handlesUnstructured: false,
    timeToMigrate: 'Hours – days',
    bestFor: 'Clean databases with simple field-to-field mapping needs',
  },
  {
    id: 'import2',
    name: 'Import2',
    focus: 'Fast, cheap, simple migrations for small teams',
    pricing: '$99 – $499',
    notesHandling: 'Skipped or moved as raw text',
    selfService: true,
    handlesUnstructured: false,
    timeToMigrate: 'Minutes – hours',
    bestFor: 'Solo agents, real estate, <2,500 records, clean data',
  },
  {
    id: 'trujay',
    name: 'Trujay / Data2CRM',
    focus: 'Done-for-you with human verification',
    pricing: '$1,500 – $9,000',
    notesHandling: 'Humans manually fix the notes-to-fields problem',
    selfService: false,
    handlesUnstructured: 'Partial (manual)',
    timeToMigrate: 'Days – weeks',
    bestFor: 'Mid-market that wants a human in the loop and has budget',
  },
  {
    id: 'enterprise',
    name: 'Slalom / Deloitte / Accenture',
    focus: 'Custom-built ETL scripts for Fortune 500',
    pricing: '$25,000 – $150,000+',
    notesHandling: 'Whatever the engineers write code for',
    selfService: false,
    handlesUnstructured: 'Yes (with cost)',
    timeToMigrate: 'Weeks – months',
    bestFor: 'Fortune 500 with custom objects, compliance reviews, and budget',
  },
];

export default function MigrationComparePage() {
  // SEO + OG
  useEffect(() => {
    const prevTitle = document.title;
    document.title =
      'MigrationCrunch vs MigrateMyCRM, Import2, Trujay — CRM Migration Software Compared';
    const desc =
      'The only CRM migration tool that extracts contacts, phones, and emails buried inside your notes into structured fields. Self-service from $599 — competitors start at $1,500 for that.';
    const upsertMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      let created = false;
      let prev = null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created = true;
      } else {
        prev = el.getAttribute('content');
      }
      el.setAttribute('content', content);
      return () => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute('content', prev);
      };
    };
    const url = `${window.location.origin}/migrate/compare`;
    const img = `${window.location.origin}/og-default.svg`;
    const restorers = [
      upsertMeta('name', 'description', desc),
      upsertMeta('property', 'og:title', document.title),
      upsertMeta('property', 'og:description', desc),
      upsertMeta('property', 'og:url', url),
      upsertMeta('property', 'og:type', 'website'),
      upsertMeta('property', 'og:site_name', 'MigrationCrunch'),
      upsertMeta('property', 'og:image', img),
      upsertMeta('name', 'twitter:card', 'summary_large_image'),
      upsertMeta('name', 'twitter:title', document.title),
      upsertMeta('name', 'twitter:description', desc),
      upsertMeta('name', 'twitter:image', img),
    ];
    return () => {
      document.title = prevTitle;
      restorers.forEach((fn) => fn());
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0F0F1A] text-white" data-testid="mc-compare-page">
      {/* Shared MigrationCrunch nav — mirror of MigrationCrunchPage header */}
      <header className="border-b border-white/10 px-6 py-4 sticky top-0 bg-[#0F0F1A]/95 backdrop-blur-sm z-30">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <Link to="/migrate" className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="mc-nav-logo">
            <Database size={28} weight="fill" className="text-[#F39C12]" />
            <span className="text-xl font-black">Migration<span className="text-[#F39C12]">Crunch</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <Link to="/migrate#fit-check" className="text-white/70 hover:text-amber-300 transition-colors" data-testid="mc-nav-fit">
              Fit check
            </Link>
            <Link to="/migrate/compare" className="text-amber-300 font-semibold" data-testid="mc-nav-compare-current">
              Compare vendors
            </Link>
            <Link to="/migrate#pricing" className="text-white/70 hover:text-amber-300 transition-colors" data-testid="mc-nav-pricing">
              Pricing
            </Link>
            <a href="/" className="text-sm text-white/70 hover:text-white border border-white/20 hover:border-white/40 rounded px-3 py-1.5 transition-colors" data-testid="mc-nav-try-crm">
              Try SalesCrunchCRM →
            </a>
          </nav>
          <a href="/" className="md:hidden text-xs text-white/70 hover:text-white border border-white/20 rounded px-2 py-1 transition-colors" data-testid="mc-nav-try-crm-mobile">
            CRM →
          </a>
        </div>
      </header>

      {/* HERO */}
      <section className="px-6 py-16 md:py-20 border-b border-white/10">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest text-amber-300 uppercase mb-3">
            <Sparkle size={12} weight="fill" /> Patent-pending architecture
          </div>
          <h1 className="text-4xl md:text-6xl font-black mb-5 leading-tight">
            We move the data{' '}
            <span className="text-amber-400">other tools leave behind.</span>
          </h1>
          <p className="text-lg md:text-xl text-white/75 max-w-3xl mx-auto leading-relaxed">
            Every other migration tool maps fields to fields. We extract names, phones,
            emails, and relationships buried{' '}
            <em className="text-white">inside your typed notes</em> — and load them into
            structured fields in your new CRM. Nobody else does this.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="/migrate#fit-check"
              className="px-6 py-3 bg-amber-400 hover:bg-amber-300 text-black font-bold rounded-md transition-colors inline-flex items-center gap-2"
              data-testid="mc-compare-fit-cta"
            >
              Take the 30-second fit check <ArrowRight size={16} weight="bold" />
            </a>
            <a
              href="/migrate#pricing"
              className="px-6 py-3 border border-white/20 hover:border-white/40 text-white font-medium rounded-md transition-colors inline-flex items-center gap-2"
              data-testid="mc-compare-pricing-cta"
            >
              See pricing
            </a>
          </div>
        </div>
      </section>

      {/* THE ARCHITECTURE DIFFERENCE */}
      <section className="px-6 py-16 bg-white/[0.02] border-b border-white/10">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-3">
            Two ways to migrate a CRM. <span className="text-amber-400">One actually works.</span>
          </h2>
          <p className="text-center text-white/60 max-w-3xl mx-auto mb-12 text-sm md:text-base">
            The dirty secret of the migration industry: 90% of the value of your old CRM
            isn&apos;t in the fields — it&apos;s in the freeform notes you typed during phone calls
            over the last 20 years. Everyone else loses that data. We don&apos;t.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {/* OLD WAY — outcome only, no method exposed */}
            <div className="bg-rose-500/[0.04] border border-rose-500/20 rounded-lg p-6 md:p-8" data-testid="mc-compare-old-way">
              <div className="flex items-center gap-2 mb-4">
                <X size={20} weight="bold" className="text-rose-400" />
                <span className="text-rose-400 text-xs font-black uppercase tracking-widest">
                  What every other tool delivers
                </span>
              </div>
              <h3 className="text-xl font-black mb-3">
                Empty fields. Lost relationships.
              </h3>
              <p className="text-sm text-white/75 mb-5 leading-relaxed">
                Conventional migration tools only move data that&apos;s already in tidy
                columns. Anything you typed inside a freeform note over the years —
                names of decision-makers, phone numbers, email addresses, referral
                chains — gets dropped into a generic text blob and stays there
                forever. Unstructured. Unsearchable. Effectively lost.
              </p>
              <div className="bg-black/40 border border-rose-500/20 rounded p-4 text-xs font-mono text-rose-200/80">
                <div className="text-white/40 mb-2">// What lands in your new CRM</div>
                Contact: Alice Wong<br />
                Phone: <span className="text-rose-300">(empty)</span><br />
                Email: <span className="text-rose-300">(empty)</span><br />
                Notes: <span className="text-rose-300">&quot;Met someone at a partner company last Tuesday — got a referral from them...&quot;</span>
                <div className="text-white/40 mt-2">// People, contacts, relationships — all buried in a text blob.</div>
              </div>
              <p className="text-xs text-rose-200/80 mt-4 leading-relaxed">
                Industry average accuracy on unstructured data: <strong>under 50%</strong>.
                That&apos;s why competitors charge $1,500–$9,000 to have a human manually fix it.
              </p>
            </div>

            {/* NEW WAY — outcome only, method intentionally not described */}
            <div className="bg-emerald-500/[0.04] border border-emerald-500/30 rounded-lg p-6 md:p-8" data-testid="mc-compare-new-way">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle size={20} weight="fill" className="text-emerald-400" />
                <span className="text-emerald-400 text-xs font-black uppercase tracking-widest">
                  What MigrationCrunch delivers
                </span>
              </div>
              <h3 className="text-xl font-black mb-3">
                Filled fields. Recovered contacts. Patent-pending.
              </h3>
              <p className="text-sm text-white/75 mb-5 leading-relaxed">
                Our patent-pending AI recovers names, phone numbers, email addresses,
                and referral relationships that live <em>inside</em> your typed notes —
                and places each one in its proper structured field in your new CRM.
                The data that was effectively lost becomes searchable, sortable, and
                actionable. (We don&apos;t describe the technical method publicly while
                the patent is pending.)
              </p>
              <div className="bg-black/40 border border-emerald-500/20 rounded p-4 text-xs font-mono text-emerald-200/90">
                <div className="text-white/40 mb-2">// What lands in your new CRM</div>
                Contact: <strong>Alice Wong</strong><br />
                &nbsp;&nbsp;Phone: <span className="text-emerald-300">populated ✓</span><br />
                &nbsp;&nbsp;Email: <span className="text-emerald-300">populated ✓</span><br />
                Contact: <strong>(referral contact recovered from note)</strong><br />
                &nbsp;&nbsp;Phone, email, title: <span className="text-emerald-300">all populated ✓</span><br />
                Relationship: <span className="text-emerald-300">linked across contacts ✓</span>
                <div className="text-white/40 mt-2">// Every entity in your notes — restructured into fields you can actually use.</div>
              </div>
              <p className="text-xs text-emerald-200/90 mt-4 leading-relaxed">
                Internal benchmark accuracy on unstructured data:{' '}
                <strong className="text-emerald-300">99% across structured AND unstructured fields.</strong>{' '}
                That&apos;s the moat.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* EXECUTIVE SUMMARY — printable side-by-side for buyers + Grayson's outbound */}
      <PrintableComparison
        variant="dark"
        testid="mc-exec-compare"
        title="Migration tools at a glance"
        subtitle="The fast version. Below this section you'll find a deeper-dive per-vendor breakdown and a tier-by-tier price match."
        shareSubject="MigrationCrunch vs MigrateMyCRM / Trujay / Import2 / consultants"
        columns={[
          { id: 'mc',  label: 'MigrationCrunch',        sub: 'AI · self-service',         isUs: true },
          { id: 'mmc', label: 'MigrateMyCRM',           sub: 'SyncMatters'                            },
          { id: 'i2',  label: 'Import2',                sub: 'Solo / SMB'                             },
          { id: 'tj',  label: 'Trujay',                 sub: 'Data2CRM'                               },
          { id: 'con', label: 'Consultant / Agency',    sub: 'Slalom · Deloitte · freelance'          },
        ]}
        rows={[
          { label: 'Pricing model', highlight: true, cells: {
            mc:  <span className="text-emerald-400 font-bold">Flat tier fee — never per record</span>,
            mmc: 'Per record (each note counts)',
            i2:  'Per record',
            tj:  'Per project + hourly',
            con: 'Hourly · $150 – $250 / hr',
          }},
          { label: '12,500 contacts', cells: {
            mc:  <span className="text-emerald-400 font-bold">$599</span>,
            mmc: '$349 – $2,500+',
            i2:  '$400 – $1,200',
            tj:  '$1,500 – $3,000',
            con: '$3,000+',
          }},
          { label: '50,000 contacts (active history)', highlight: true, cells: {
            mc:  <span className="text-emerald-400 font-bold">$1,799</span>,
            mmc: '$4,000 – $8,000',
            i2:  '$2,500+',
            tj:  '$3,500 – $5,000',
            con: '$5,000 – $9,000',
          }},
          { label: '100,000 contacts', cells: {
            mc:  <span className="text-emerald-400 font-bold">$4,499</span>,
            mmc: '$10,000 – $20,000+',
            i2:  '$5,000+',
            tj:  '$5,000 – $9,000',
            con: '$10,000 – $25,000',
          }},
          { label: '500,000+ contacts', cells: {
            mc:  <span className="text-emerald-400 font-bold">$7,999</span>,
            mmc: '$25,000+ (custom)',
            i2:  'Out of scope',
            tj:  'Custom quote',
            con: '$75,000 – $150,000+',
          }},
          { label: 'Notes & history handling', highlight: true, cells: {
            mc:  <span className="text-emerald-400">AI extracts entities into structured fields</span>,
            mmc: 'Dumped into a "Description" blob',
            i2:  'Skipped or moved as raw text',
            tj:  'Humans manually fix it (slow)',
            con: 'Depends on what they code',
          }},
          { label: 'Free if destination = SalesCrunchCRM', cells: {
            mc:  <span className="text-emerald-400 font-bold">Yes — $0</span>,
            mmc: 'n/a',
            i2:  'n/a',
            tj:  'n/a',
            con: 'n/a',
          }},
          { label: 'Time to migrate', cells: {
            mc:  <span className="text-emerald-400">Minutes</span>,
            mmc: 'Hours – days',
            i2:  'Minutes – hours',
            tj:  'Days – weeks',
            con: 'Weeks – months',
          }},
          { label: 'Accuracy on notes', cells: {
            mc:  <span className="text-emerald-400 font-bold">99% (patent-pending AI)</span>,
            mmc: 'Best-effort text copy',
            i2:  'Standard',
            tj:  'Manual cleanup',
            con: 'Engineer-dependent',
          }},
          { label: 'Money-back guarantee', cells: {
            mc:  <span className="text-emerald-400 font-bold">30-day on note recovery</span>,
            mmc: 'No',
            i2:  'No',
            tj:  'No',
            con: 'Contract-dependent',
          }},
        ]}
        footnote="Competitor pricing reflects public quotes as of Feb 2026 for databases with average ~8 records per contact (typical SMB sales team with 3 years of activity). Refresh this comparison quarterly."
      />


      <section className="px-6 py-16 border-b border-white/10">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-3">
            How we stack up
          </h2>
          <p className="text-center text-white/60 mb-10 text-sm md:text-base max-w-3xl mx-auto">
            Pricing reflects publicly published rates as of February 2026. Architecture
            descriptions are based on each vendor&apos;s public documentation and our own
            testing.
          </p>

          <div className="overflow-x-auto rounded-lg border border-white/10">
            <table className="w-full text-sm" data-testid="mc-compare-table">
              <thead>
                <tr className="bg-white/5">
                  <th className="text-left p-3 font-bold text-white/60 text-xs uppercase tracking-wider">Vendor</th>
                  <th className="text-left p-3 font-bold text-white/60 text-xs uppercase tracking-wider">Pricing (2026)</th>
                  <th className="text-left p-3 font-bold text-white/60 text-xs uppercase tracking-wider">Handles notes?</th>
                  <th className="text-left p-3 font-bold text-white/60 text-xs uppercase tracking-wider">Self-service</th>
                  <th className="text-left p-3 font-bold text-white/60 text-xs uppercase tracking-wider">Time to migrate</th>
                </tr>
              </thead>
              <tbody>
                {COMPETITORS.map((c) => (
                  <tr
                    key={c.id}
                    className={
                      c.isUs
                        ? 'bg-amber-500/10 border-t border-amber-500/30'
                        : 'border-t border-white/5 hover:bg-white/[0.02]'
                    }
                    data-testid={`mc-compare-row-${c.id}`}
                  >
                    <td className="p-3 align-top">
                      <div className={`font-bold ${c.isUs ? 'text-amber-300' : 'text-white'}`}>
                        {c.name}
                        {c.isUs && (
                          <span className="ml-2 text-[10px] bg-amber-400 text-black px-2 py-0.5 rounded-full uppercase tracking-wider font-black">
                            us
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-white/50 mt-1">{c.focus}</div>
                    </td>
                    <td className="p-3 align-top text-white/80 font-mono text-xs">
                      {c.pricing}
                    </td>
                    <td className="p-3 align-top text-xs">
                      {c.handlesUnstructured === true ? (
                        <span className="text-emerald-400 font-bold inline-flex items-center gap-1">
                          <CheckCircle size={14} weight="fill" /> AI auto-extracts
                        </span>
                      ) : c.handlesUnstructured === false ? (
                        <span className="text-rose-400 inline-flex items-center gap-1">
                          <X size={14} weight="bold" /> Lost in blob
                        </span>
                      ) : (
                        <span className="text-amber-300 inline-flex items-center gap-1">
                          <WarningCircle size={14} weight="fill" /> Manual (paid)
                        </span>
                      )}
                      <div className="text-[11px] text-white/45 mt-1">{c.notesHandling}</div>
                    </td>
                    <td className="p-3 align-top text-xs">
                      {c.selfService ? (
                        <span className="text-emerald-400 inline-flex items-center gap-1">
                          <CheckCircle size={14} weight="fill" /> Yes
                        </span>
                      ) : (
                        <span className="text-rose-400 inline-flex items-center gap-1">
                          <X size={14} weight="bold" /> Sales call required
                        </span>
                      )}
                    </td>
                    <td className="p-3 align-top text-xs text-white/80">{c.timeToMigrate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* PER-TIER BREAKDOWN — every MigrationCrunch tier vs the closest
          equivalent at each of the 4 competitors. This is the section the
          user explicitly asked for: "expand the compare vendors page to
          include each tier." */}
      <section className="px-6 py-16 border-b border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center gap-2 text-[10px] font-bold tracking-widest text-amber-300 uppercase mb-3">
            <Scales size={12} weight="fill" /> Tier-by-tier
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-center mb-3">
            Each tier, each competitor, side by side
          </h2>
          <p className="text-center text-white/60 mb-10 text-sm md:text-base max-w-3xl mx-auto">
            For every MigrationCrunch tier, here is the nearest equivalent at
            each competitor — by data volume, not by SKU name. Public pricing
            as of February 2026.
          </p>

          <div className="overflow-x-auto rounded-lg border border-white/10">
            <table
              className="w-full text-xs md:text-sm"
              data-testid="mc-compare-tier-breakdown-table"
            >
              <thead>
                <tr className="bg-white/5">
                  <th className="text-left p-3 font-bold text-amber-300 text-[10px] md:text-xs uppercase tracking-wider w-1/5">
                    MigrationCrunch tier
                  </th>
                  <th className="text-left p-3 font-bold text-white/60 text-[10px] md:text-xs uppercase tracking-wider">
                    MigrateMyCRM
                  </th>
                  <th className="text-left p-3 font-bold text-white/60 text-[10px] md:text-xs uppercase tracking-wider">
                    Import2
                  </th>
                  <th className="text-left p-3 font-bold text-white/60 text-[10px] md:text-xs uppercase tracking-wider">
                    Trujay
                  </th>
                  <th className="text-left p-3 font-bold text-white/60 text-[10px] md:text-xs uppercase tracking-wider">
                    Slalom / Big-4
                  </th>
                </tr>
              </thead>
              <tbody>
                {TIER_BREAKDOWN.map((row, i) => (
                  <tr
                    key={i}
                    className="border-t border-white/5 align-top hover:bg-white/[0.02]"
                    data-testid={`mc-compare-tier-breakdown-row-${i}`}
                  >
                    <td className="p-3">
                      <div className="font-black text-amber-300 text-xs md:text-sm">
                        {row.ours.name}
                      </div>
                      <div className="font-mono text-[11px] md:text-xs text-white/85 mt-1">
                        {row.ours.price}
                      </div>
                      <div className="text-[10px] md:text-[11px] text-white/45 mt-0.5">
                        {row.ours.range}
                      </div>
                    </td>
                    {['migratemycrm', 'import2', 'trujay', 'slalom'].map((slug) => (
                      <td key={slug} className="p-3 align-top">
                        <div className="font-semibold text-white/85 text-[11px] md:text-xs">
                          {row[slug].name}
                        </div>
                        <div className="font-mono text-[11px] md:text-xs text-white/65 mt-1">
                          {row[slug].price}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-[11px] text-white/35 mt-3 text-center">
            Tiers matched by data volume; named SKU on each competitor may
            differ. Pricing reflects publicly published rates as of February 2026.
          </p>

          {/* Deep-link card grid — sends each competitor-search visitor to a
              dedicated /migrate/vs/<slug> page with their own full comparison. */}
          <div className="mt-10">
            <h3 className="text-base md:text-lg font-black text-white text-center mb-4">
              Looking up a specific competitor? Jump straight to the full head-to-head:
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {Object.values(VS_COMPETITORS).map((c) => (
                <Link
                  key={c.slug}
                  to={`/migrate/vs/${c.slug}`}
                  className="bg-white/5 hover:bg-white/10 border border-white/10 hover:border-amber-400/40 rounded-md p-4 transition-colors group"
                  data-testid={`mc-compare-vs-card-${c.slug}`}
                >
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">
                    Compare
                  </div>
                  <div className="font-black text-white text-sm group-hover:text-amber-300 transition-colors">
                    vs {c.name}
                  </div>
                  <div className="text-[11px] text-white/50 mt-1 line-clamp-2 leading-relaxed">
                    {c.tagline}
                  </div>
                  <div className="text-[10px] text-amber-300/80 mt-2 inline-flex items-center gap-1 font-bold">
                    See full breakdown <ArrowRight size={10} weight="bold" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY WE WIN — FOUR PILLARS */}
      <section className="px-6 py-16 bg-white/[0.02] border-b border-white/10">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-3">
            Four things only we do.
          </h2>
          <p className="text-center text-white/60 mb-12 text-sm md:text-base">
            Every one of these is a defensible competitive moat. Not aspirations — shipped.
          </p>

          <div className="grid md:grid-cols-2 gap-5">
            <Pillar
              icon={<Brain size={28} weight="fill" />}
              tone="amber"
              title="Notes → Fields, automatically"
              body={
                <>
                  When your 20-year-old note says <em>&quot;Sarah at Acme, 555-0123&quot;</em>, we
                  create Sarah as a contact, set her phone, and link the note to her —
                  without you lifting a finger. Competitors leave that as text. We&apos;ve
                  filed for patent protection on the architecture.
                </>
              }
              testid="pillar-notes"
            />
            <Pillar
              icon={<Lightning size={28} weight="fill" />}
              tone="emerald"
              title="Minutes, not weeks (real numbers)"
              body={
                <>
                  100,000 contacts in under 10 minutes. Done. The mid-market &quot;done-for-you&quot;
                  competitors quote 2–6 weeks because humans hand-clean the notes problem
                  we automate. The Fortune 500 agencies quote 2–4 months for the same job.
                </>
              }
              testid="pillar-speed"
            />
            <Pillar
              icon={<CurrencyDollar size={28} weight="fill" />}
              tone="emerald"
              title="Complexity ≠ price"
              body={
                <>
                  Everyone else charges more for &quot;messy data.&quot; We don&apos;t. Our $599 tier
                  handles a clean Salesforce export <em>and</em> a 20-year tangled mess of
                  notes at the same price. The AI is doing the work, not a billable human.
                </>
              }
              testid="pillar-pricing"
            />
            <Pillar
              icon={<Lock size={28} weight="fill" />}
              tone="purple"
              title="We tell you when we&apos;re NOT the right fit"
              body={
                <>
                  Take our <Link to="/migrate#fit-check" className="text-amber-300 underline hover:text-amber-200">30-second fit check</Link> —
                  if your shop runs on custom Salesforce workflows and complex automations,
                  we&apos;ll tell you to hire a human consultant instead. Nobody else will say that
                  out loud. We do, because we&apos;d rather lose the sale than the trust.
                </>
              }
              testid="pillar-honesty"
            />
          </div>
        </div>
      </section>

      {/* PRICING DISRUPTION MATH */}
      <section className="px-6 py-16 border-b border-white/10">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-3">
            The math doesn&apos;t work for our competitors.
          </h2>
          <p className="text-center text-white/60 mb-10 text-sm md:text-base">
            We didn&apos;t price low because we&apos;re a startup. We priced low because AI eats
            the line items they have to charge humans for.
          </p>

          <div className="space-y-4">
            <PricingRow
              tier="Starter Pro"
              ourPrice="$599"
              theirEquivalent="MigrateMyCRM Plus ($999) — but with no AI on notes"
              savings="Save $400 + your notes get extracted"
            />
            <PricingRow
              tier="Growth AI"
              ourPrice="$1,799"
              theirEquivalent="Trujay mid-tier ($3,500–$5,000) with paid manual cleanup"
              savings="Save $1,700+ AND skip the 2-week wait"
            />
            <PricingRow
              tier="Enterprise AI"
              ourPrice="$4,499"
              theirEquivalent="Trujay high-tier ($7,000–$9,000) or boutique consultancy ($15,000+)"
              savings="Save $2,500–$10,500 with same-day delivery"
            />
            <PricingRow
              tier="Unlimited AI"
              ourPrice="$7,999"
              theirEquivalent="Slalom / Deloitte custom project ($25,000–$150,000)"
              savings="Save $17,000–$142,000. An &apos;easy approval&apos; for enterprise pilots."
            />
          </div>
        </div>
      </section>

      {/* HONEST: WHEN TO USE THEM */}
      <section className="px-6 py-16 bg-white/[0.02] border-b border-white/10">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black text-center mb-3">
            And when you should pick somebody else.
          </h2>
          <p className="text-center text-white/60 mb-10 text-sm md:text-base">
            Honesty over hype. Three real scenarios where we&apos;re not the right call:
          </p>
          <div className="space-y-3">
            <AltCard
              vendor="Nobody — do it yourself"
              scenario="You have under 50 contacts, clean data, no notes worth saving."
              why="Every modern CRM ships a free native CSV import that handles a small, clean list just fine. Don&apos;t pay anyone for that job — including us. (We used to point this row at Import2, but our $299 Contacts Only tier now covers clean lists up to 12,500 for less.)"
            />
            <AltCard
              vendor="Trujay / Data2CRM"
              scenario="You explicitly want a human in the loop verifying every row, and budget isn&apos;t a constraint."
              why="Their human-verification model is real value if you can&apos;t trust automation OR your compliance team requires manual sign-off on each record."
            />
            <AltCard
              vendor="Slalom / Deloitte / Accenture"
              scenario="Fortune 500 with hundreds of custom objects, regulated industry, multi-year roadmap, and a six-figure budget."
              why="Custom ETL engineering and change-management consulting is genuinely worth their fees at that scale. We&apos;d steer you there ourselves."
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-20">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-4">
            Stop losing 20 years of notes.
          </h2>
          <p className="text-white/70 mb-8 text-base md:text-lg">
            Take our 30-second fit check. If we&apos;re right for you, you&apos;ll know.
            If we&apos;re not, we&apos;ll tell you who is.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="/migrate#fit-check"
              className="px-6 py-3 bg-amber-400 hover:bg-amber-300 text-black font-bold rounded-md transition-colors inline-flex items-center justify-center gap-2"
              data-testid="mc-compare-bottom-fit-cta"
            >
              <Sparkle size={16} weight="fill" />
              Take the fit check
            </a>
            <Link
              to="/migrate"
              className="px-6 py-3 border border-white/20 hover:border-white/40 text-white font-medium rounded-md transition-colors inline-flex items-center justify-center gap-2"
              data-testid="mc-compare-bottom-home-cta"
            >
              See how it works
            </Link>
          </div>
          <p className="text-xs text-white/40 mt-6">
            30-day money-back guarantee on AI note recovery. One-time pricing. No subscriptions.
          </p>
        </div>
      </section>

      <MigrationCrunchFooter />
    </div>
  );
}

function Pillar({ icon, title, body, tone = 'amber', testid }) {
  const toneClass =
    tone === 'amber'
      ? 'bg-amber-500/[0.06] border-amber-500/25 text-amber-300'
      : tone === 'emerald'
      ? 'bg-emerald-500/[0.06] border-emerald-500/25 text-emerald-300'
      : 'bg-purple-500/[0.06] border-purple-500/25 text-purple-300';
  return (
    <div className={`border rounded-lg p-6 ${toneClass.split(' ').slice(0, 2).join(' ')}`} data-testid={testid}>
      <div className={toneClass.split(' ').slice(2).join(' ') + ' mb-3'}>{icon}</div>
      <h3 className="text-lg font-black mb-2 text-white">{title}</h3>
      <p className="text-sm text-white/75 leading-relaxed">{body}</p>
    </div>
  );
}

function PricingRow({ tier, ourPrice, theirEquivalent, savings }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-6" data-testid={`mc-compare-pricing-${tier.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="md:w-32 flex-shrink-0">
        <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">
          Our tier
        </div>
        <div className="font-black text-white">{tier}</div>
        <div className="text-2xl font-black text-amber-300">{ourPrice}</div>
      </div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">
          Equivalent elsewhere
        </div>
        <div className="text-sm text-white/80">{theirEquivalent}</div>
      </div>
      <div className="md:w-56 flex-shrink-0">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300/60 mb-1">
          Your win
        </div>
        <div className="text-sm font-bold text-emerald-300">{savings}</div>
      </div>
    </div>
  );
}

function AltCard({ vendor, scenario, why }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-5" data-testid={`mc-compare-alt-${vendor.toLowerCase().split(' ')[0]}`}>
      <div className="flex flex-col md:flex-row md:items-start gap-3">
        <div className="md:w-48 flex-shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">
            Use them when
          </div>
          <div className="font-black text-white">{vendor}</div>
        </div>
        <div className="flex-1">
          <p className="text-sm text-white/85 mb-2 font-medium">{scenario}</p>
          <p className="text-xs text-white/55 leading-relaxed">{why}</p>
        </div>
      </div>
    </div>
  );
}
